import { useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import type { PermissionAction } from '@atlas/shared';

export function usePermissions() {
  const permissions = useAuthStore((s) => s.permissions);

  // There was an `isAdmin` flag here, derived from `systemProfile` and
  // hardcoding a copy of ADMIN_PROFILES. It gated a handful of UI affordances
  // while `can()` deliberately ignored it — two access models side by side,
  // one of them invisible in the roles UI. Those affordances are now role
  // actions (`asset.edit-device-ids`, `*.edit-record`), so the role matrix is
  // the only model left.

  // Reusable scope check on a single permission entry — true when the entry
  // grants the action via either the full bit or the matching Own variant.
  const permGrants = (perm: any, action: PermissionAction): boolean => {
    if (!perm) return false;
    if (perm[action]) return true;
    if (action === 'view'   && perm.viewOwnRecord)   return true;
    if (action === 'edit'   && perm.editOwnRecord)   return true;
    if (action === 'delete' && perm.deleteOwnRecord) return true;
    return false;
  };

  const can = useCallback(
    (resourceSlug: string, action: PermissionAction): boolean => {
      // 1) Direct entry on the requested slug.
      const direct = permissions.find((p: any) => p.resource?.slug === resourceSlug);
      if (permGrants(direct, action)) return true;
      // 2) Parent-form fallback — mirrors the server's `resolveScope`.
      //    When the requested slug is a form, any child report/page entry
      //    that grants the action counts. This is the only way a user with
      //    delete on (say) the `dispatch_page` report can see the delete
      //    button on the dispatch dashboard, which checks the form slug.
      //    `add` only bubbles from reports (pages can't grant create).
      for (const p of permissions as any[]) {
        const r = p.resource;
        if (!r || r.parentForm !== resourceSlug) continue;
        if (action === 'add' && r.type !== 'report') continue;
        if (action !== 'add' && r.type !== 'report' && r.type !== 'page') continue;
        if (permGrants(p, action)) return true;
      }
      return false;
    },
    [permissions]
  );

  const canAny = useCallback(
    (resourceSlug: string, actions: PermissionAction[]): boolean => {
      return actions.some((action) => can(resourceSlug, action));
    },
    [can]
  );

  // Most-permissive scope the role holds for an action on a resource:
  // 'all' > 'own' > 'none'. Used by list UIs to decide whether the
  // edit/delete affordance is unconditional ('all') or must be gated to
  // rows the user owns ('own'). Deliberately permissive (mirrors `can`'s
  // parent-form bubbling, takes the max scope) — the SERVER is the real
  // enforcement boundary, so the UI must never *under*-show an affordance
  // the user legitimately has.
  const scope = useCallback(
    (resourceSlug: string, action: PermissionAction): 'all' | 'own' | 'none' => {
      const ownVariant =
        action === 'view' ? 'viewOwnRecord'
        : action === 'edit' ? 'editOwnRecord'
        : action === 'delete' ? 'deleteOwnRecord'
        : null;
      const scopeOfPerm = (perm: any): 'all' | 'own' | 'none' => {
        if (!perm) return 'none';
        if (perm[action]) return 'all';
        if (ownVariant && perm[ownVariant]) return 'own';
        return 'none';
      };
      let best: 'all' | 'own' | 'none' = scopeOfPerm(
        permissions.find((p: any) => p.resource?.slug === resourceSlug)
      );
      if (best === 'all') return 'all';
      for (const p of permissions as any[]) {
        const r = p.resource;
        if (!r || r.parentForm !== resourceSlug) continue;
        if (action === 'add' && r.type !== 'report') continue;
        if (action !== 'add' && r.type !== 'report' && r.type !== 'page') continue;
        const s = scopeOfPerm(p);
        if (s === 'all') return 'all';
        if (s === 'own') best = 'own';
      }
      return best;
    },
    [permissions]
  );

  return { can, canAny, scope };
}
